import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { frozenRateFields, rateOverrideFields, rateOverrideInputFields, rateOverridePatchFields, documentCurrencyInputFields, documentCurrencyPatchFields } from './fx.js';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { PurchaseOrderStatsSchema } from './document-stats.js';
import { DocumentDateRangeQuery, PickerIdsSchema, pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
import { SETTINGS_REGISTRY } from './org-settings.js';
const c = initContract();
/**
 * Purchase Orders (PART 7, PUR-01 on the SAL-00 shared layout).
 *
 * Permission area: `purchasing` (PART 16.2.2 row 2), per action:
 *   list/get = purchasing.view · create = purchasing.create · edit/send/
 *   cancel a pre-commitment order = purchasing.edit · delete = purchasing.delete ·
 *   approve an over-limit order = purchasing.approve (16.3: "purchasing.approve
 *   for releasing a large order" — PART 16's twelve-area grid supersedes the
 *   prototype's single `exp.approve` key, which 7.2 itself leaves to PART 16).
 *   Per 16.4.2 the Accountant and the AP Clerk both hold purchasing RCU —
 *   they raise and send orders but CANNOT approve; Administrator/Owner can.
 *
 * Lifecycle per PUR-01 §7 / ruling R8 (BR-PUR-01-11a): draft and approved are
 * pre-commitment — freely editable (an edit withdraws the approval) and
 * freely deletable. SEND is the one-way door into commitment: from there the
 * order is corrected by cancel-and-reissue, never rewritten.
 * partially_received/received are driven by goods-receipt posting
 * (BR-PUR-01-05); closed ends the flow.
 *
 * The approval gate (BR-PUR-01-01): an order STRICTLY ABOVE the limit cannot
 * be sent while draft without a purchasing.approve holder clearing it first.
 * Exactly-at-the-limit needs no approval (PUR-01 §15's boundary). The check
 * only applies while Draft (BR-PUR-01-04).
 *
 * An order posts NOTHING to the ledger (BR-PUR-01-11, R9: it appears in no
 * financial report). Its on-order quantity is not stored either — the
 * reorder panel sums open sent/partially-received orders live.
 */
/**
 * PART 16.5.4 L-1 — the purchase-approval limit, and BR-PERM-14's whole
 * requirement: "the threshold must be a company setting, not a number written
 * into the code." Since PART 15 landed this is
 * the COMPANY SETTING `treasury.po_approval_limit` (BR-PUR-01-02, OQ-16-05);
 * this constant is only its registry default, kept for the UI's optimistic
 * fallback while the effective settings load. The gate itself reads the
 * setting per request, and (BR-PUR-01-03, closed with FIN-05) compares a
 * foreign order's total IN THE BASE CURRENCY — divided by the rate
 * effective on the order date.
 *
 * BR-PERM-14 leaves it "[NOT IN PROTOTYPE — decide at build time] whether it
 * is editable" and proposes the Invoices & Sales settings group. Built as
 * editable, in the Treasury & Currency group — OQ-16-05's answer, and the
 * group a money ceiling belongs to. The `extension: true` flag on the
 * registry entry is what marks it as beyond the shipped ADM-01 set.
 */
export const PO_APPROVAL_LIMIT = SETTINGS_REGISTRY['treasury.po_approval_limit']
    .default;
/** '25000.00' → '25,000' — how the BR-PUR-01-01 sentence names the limit. */
export function formatApprovalLimit(limit) {
    const [integer = '0'] = limit.split('.');
    return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
// ── The fixed sentences of PUR-01 ───────────────────────────────────────────
export const PO_MESSAGES = {
    /**
     * BR-PUR-01-01 / ERR-PUR-01-01 — the spec's sentence, now naming the
     * company's own limit (at the default it reads exactly as the spec quotes).
     */
    approvalRequired: (limit = PO_APPROVAL_LIMIT) => `This order exceeds the ${formatApprovalLimit(limit)} USD approval limit — manager approval required before sending to the supplier.`,
    /** BR-PUR-01-08 / ERR-PUR-01-03. */
    closedNoBill: (code) => `${code} is closed. It cannot be received or billed again.`,
    /**
     * R8 / BR-PUR-01-11a — the sent-commitment lock. Wave 1C reworded it to
     * say only what was true then (cancel-and-reissue was the one correction).
     * Wave 5E built the amendment flow (`reopen`), so the sentence now names
     * both corrections and the boundary between them — and is still only what
     * is true.
     */
    sentLocked: 'This order has been sent to the supplier. While nothing has been received, billed or shipped against it, Amend returns it to draft and the supplier gets a revised copy on the next send; after that, the correction is to cancel and reissue it.',
    /** A terminal order is read-only. */
    terminalLocked: 'This order is finished and read-only.',
    /** BR-PUR-01-12 — the party fields must hold a supplier. */
    notASupplier: (name) => `${name} is not a supplier and cannot be picked on a purchase document.`,
    /** BR-SAL-00-15 shape for the send door. */
    noLines: 'Add at least one line before sending.',
    /** Approve is only meaningful on an over-limit draft. */
    nothingToApprove: 'Only a draft order above the approval limit needs approval.',
    /** Sending is a draft/approved-only move. */
    notSendable: 'Only a draft or approved order can be sent to the supplier.',
    /** Cancelling a billed order would orphan the bills. */
    cancelHasBills: (code) => `${code} has posted bills against it and cannot be cancelled. Correct the bills with a debit note first.`,
    /** R8: deletion is pre-commitment only. */
    sentNoDelete: 'A sent order cannot be deleted. Cancel it instead.',
    /** GEN-59: the cancelling act carries its reason. */
    cancelReasonRequired: 'An order can only be cancelled with a reason, which is stored on the order and written to the audit log.',
    /** BR-PUR-01-10 / ERR-PUR-01-06 — the chase nudge, word for word. */
    overdueNudge: (date) => `Expected ${date}. Nothing has arrived. Chase the supplier?`,
    /**
     * Wave 1C — BR-PUR-01-10's field is what the late flag and the chase nudge
     * both read, so a commitment without it is a commitment nothing can chase.
     * Required at the send door, mirrored on the client.
     */
    expectedDateRequired: 'Enter the expected delivery date before sending to the supplier.',
    // ── wave 5E: amend a sent order ───────────────────────────────────────────
    /** GEN-59's shape on the amend act: the reason rides the audit row. */
    reopenReasonRequired: 'An order can only be amended with a reason, which is written to the audit log.',
    /** Only a SENT order with nothing against it goes back to draft. */
    notReopenable: 'Only a sent order with nothing received, billed or shipped against it can be amended. Cancel and reissue instead.',
    /**
     * The blocking document, by its printed code: a posted goods receipt, a
     * posted bill or a live import order means the commitment has been acted
     * on, and the correction is cancel-and-reissue (or reversing that
     * document), never a rewrite under it.
     */
    reopenBlocked: (code) => `${code} has been raised against this order, so it cannot be amended. Cancel and reissue the order instead.`,
};
/**
 * D46 — PUR-01's CONVERTED parameterised refusal, structured. The `limit`
 * param is PRE-FORMATTED with the sentence's own `formatApprovalLimit`, so
 * the ICU value stays a plain `{limit}` and the two renderings cannot
 * disagree about grouping. This is the member whose DEFAULT-argument
 * sentence used to be an exact-match map entry (`po.approvalBanner`) that a
 * changed company limit silently un-localized — the brittleness D46 retires.
 */
export const PO_REFUSALS = {
    approvalRequired: (limit = PO_APPROVAL_LIMIT) => ({
        message: PO_MESSAGES.approvalRequired(limit),
        key: 'po.approvalRequired',
        params: { limit: formatApprovalLimit(limit) },
    }),
    // ── d46tail: PUR-01's two remaining parameterised refusals ────────────────
    /** BR-PUR-01-12 — the picked party is not a supplier; `name` is a display
     *  value. Raised at the payment, bill and order doors. */
    notASupplier: (name) => ({
        message: PO_MESSAGES.notASupplier(name),
        key: 'po.errNotASupplier',
        params: { name },
    }),
    /** Cancelling a billed order would orphan its bills; `code` is the order's
     *  formatted document code. */
    cancelHasBills: (code) => ({
        message: PO_MESSAGES.cancelHasBills(code),
        key: 'po.errCancelHasBills',
        params: { code },
    }),
    /** Wave 5E — `code` is the BLOCKING document's printed code (GRN, bill or
     *  import reference), a display value. */
    reopenBlocked: (code) => ({
        message: PO_MESSAGES.reopenBlocked(code),
        key: 'po.errReopenBlocked',
        params: { code },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const PurchaseOrderStatusSchema = z.enum([
    'draft',
    'approved',
    'sent',
    'partially_received',
    'received',
    'closed',
    'cancelled',
]);
/** One grid line — item-linked (itemId nullable, description the fallback). */
export const PurchaseOrderLineInputSchema = z
    .object({
    itemId: z.uuid().optional(),
    description: z.string().trim().min(1).max(200),
    qty: QtyStringSchema,
    unitPrice: MoneyStringSchema,
    discountPct: PctStringSchema.optional(),
    /**
     * Wave 4C: the unit the qty was typed in — the item's stock unit or one of
     * its own Buy-allowed units; absent = the stock unit. The server stores
     * the stock-unit conversion beside it (BR-MD-03-04d).
     */
    unitId: z.uuid().optional(),
})
    .strict();
/** F-2/F-4's two-way pair, exactly as on the sales documents. */
export const PoTwoWayAmountSchema = z.union([
    z.object({ pct: PctStringSchema }).strict(),
    z.object({ amount: MoneyStringSchema }).strict(),
]);
export const PurchaseOrderLineSchema = z
    .object({
    lineNo: z.number().int().positive(),
    itemId: z.uuid().nullable(),
    itemCode: z.string().nullable(),
    description: z.string(),
    qty: z.string(),
    unitPrice: z.string(),
    discountPct: z.string(),
    lineTotal: z.string(),
    /** Wave 4C: the typed unit; null = the item's stock unit (or a free-text line). */
    unitId: z.uuid().nullable(),
    /** qty in the item's stock unit (BR-MD-03-04d); null on a free-text line or a line from before the column. */
    baseQty: z.string().nullable(),
    /** The unit `qty` is in — the typed unit, else the item's stock unit; both null on a free-text line. */
    unitSymbol: z.string().nullable(),
    unitPrecision: UnitPrecisionSchema.nullable(),
    /**
     * The GRN progress figures (BR-PUR-02-02): Σ received / Σ accepted over
     * POSTED goods receipts against this line. '0' until a receipt posts.
     */
    receivedQty: z.string(),
    acceptedQty: z.string(),
})
    .strict();
export const PurchaseOrderSchema = z
    .object({
    id: z.uuid(),
    /** PO-#### — its own series (R3/§6.8). */
    code: z.string(),
    orderNo: z.number().int().positive(),
    status: PurchaseOrderStatusSchema,
    /** GEN-59: why the order was cancelled — null until it is, and on rows cancelled before the reason existed. */
    cancelReason: z.string().nullable(),
    orderDate: IsoDateSchema,
    /** BR-PUR-01-10. */
    expectedDate: IsoDateSchema.nullable(),
    partnerId: z.uuid(),
    partnerCode: z.string(),
    partnerName: z.string(),
    currency: z.string(),
    /** EC-19-10: the rate this document froze when it was confirmed, and its "as of" date. */
    ...frozenRateFields,
    /** Wave 4D — the typed-rate flag and the market hint (fx.ts). */
    ...rateOverrideFields,
    lines: PurchaseOrderLineSchema.array(),
    subtotal: z.string(),
    discountPct: z.string(),
    discountAmount: z.string(),
    taxPct: z.string(),
    taxAmount: z.string(),
    grandTotal: z.string(),
    /**
     * The three-figure strip: Σ grand totals of POSTED bills against this
     * order (R9 — drafts bill nothing), whether that covers the order, and
     * the GRN leg — Σ ACCEPTED value over posted receipts at the order's
     * prices (BR-PUR-02-06). receivedTotal is null exactly while NO posted
     * receipt exists (the strip's dash).
     */
    billedTotal: z.string(),
    receivedTotal: z.string().nullable(),
    fullyBilled: z.boolean(),
    /**
     * Wave 1C — billed STRICTLY above ordered. `fullyBilled` stays true
     * here too (nothing is left to bill), but the figure is drawn amber, not
     * green: more was billed than was ordered.
     */
    overBilled: z.boolean(),
    /**
     * Wave 1C — the order's value EXCLUDING VAT: subtotal minus the document
     * discount. `grandTotal` includes VAT, while the GRN leg (`receivedTotal`)
     * is Σ line value excluding VAT — so this is the figure the received leg
     * is comparable with, and the screens label both bases explicitly.
     */
    orderedNet: z.string(),
    /**
     * BR-PUR-01-01/-04: strictly above the limit AND still draft — the
     * client draws the amber banner (or the approver's green one) from this.
     */
    needsApproval: z.boolean(),
    approvedBy: z.uuid().nullable(),
    approvedAt: z.string().nullable(),
    sentAt: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
export const CreatePurchaseOrderRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverrideInputFields,
    /** Per-document currency (fx.ts documentCurrencyInputFields). */
    ...documentCurrencyInputFields,
    partnerId: z.uuid(),
    orderDate: IsoDateSchema,
    expectedDate: IsoDateSchema.optional(),
    lines: PurchaseOrderLineInputSchema.array().min(1),
    discount: PoTwoWayAmountSchema.optional(),
    tax: PoTwoWayAmountSchema.optional(),
    notes: z.string().max(2000).optional(),
})
    .strict();
export const UpdatePurchaseOrderRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverridePatchFields,
    /** Per-document currency, draft only (fx.ts documentCurrencyPatchFields). */
    ...documentCurrencyPatchFields,
    orderDate: IsoDateSchema.optional(),
    expectedDate: IsoDateSchema.nullable().optional(),
    /** The grid saves as a set: present = replace all lines (pre-commitment only). */
    lines: PurchaseOrderLineInputSchema.array().min(1).optional(),
    discount: PoTwoWayAmountSchema.optional(),
    tax: PoTwoWayAmountSchema.optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
/**
 * What Send to supplier answers — the sent order, plus whether the paper went
 * out by mail (wave 2 of specs/erp-gap-analysis.md). `emailedTo` is the
 * supplier's address when a message carrying the order's PDF was queued, and
 * null when the supplier has no address on file — an ordinary MD-01 state,
 * answered rather than refused, because the order IS sent either way and the
 * screen's job is to say which happened.
 */
export const SendPurchaseOrderResponseSchema = PurchaseOrderSchema.extend({
    emailedTo: z.string().nullable(),
}).strict();
const orderParams = z.object({ orderId: z.uuid() }).strict();
export const purchaseOrdersContract = c.router({
    list: {
        method: 'GET',
        path: '/api/purchases/orders',
        query: pagedQuery({
            partnerId: z.uuid().optional(),
            status: PurchaseOrderStatusSchema.optional(),
            /** GEN-50: the printed order code, or the supplier's name. */
            q: z.string().optional(),
            /**
             * BR-PUR-02's two statuses as ONE predicate — sent, or already
             * part-received. The GRN picker narrowed to these in JavaScript,
             * which a page turns into a lie about how many orders are open.
             */
            receivable: z.enum(['true', 'false']).optional(),
            /** GEN-50: the order a GRN or an import form is already holding. */
            ids: PickerIdsSchema.optional(),
            /** Wave 3B — bounds on the order date, both inclusive. */
            ...DocumentDateRangeQuery,
        }),
        responses: {
            200: pagedResponse('orders', PurchaseOrderSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Purchase orders (purchasing.view), FILTERED IN SQL AND PAGED (NFR-42) and the "which order?" typeahead (GEN-50).',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail: the purchase orders on either side of
     * this one in number order (null at an end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/purchases/orders/:orderId/neighbours',
        pathParams: orderParams,
        responses: {
            200: DocumentNeighboursSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next purchase order by number (purchasing.view).',
    },
    /** S4 — the "Go to no." box: one purchase order by its printed number; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/purchases/orders/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One purchase order by its printed number (purchasing.view).',
    },
    get: {
        method: 'GET',
        path: '/api/purchases/orders/:orderId',
        pathParams: orderParams,
        responses: {
            200: PurchaseOrderSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One order with its lines (purchasing.view).',
    },
    create: {
        method: 'POST',
        path: '/api/purchases/orders',
        body: CreatePurchaseOrderRequestSchema,
        responses: {
            201: PurchaseOrderSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft order (purchasing.create). Partner must be an active supplier (BR-PUR-01-12); totals per PART 18 F-1..F-7.',
    },
    update: {
        method: 'PATCH',
        path: '/api/purchases/orders/:orderId',
        pathParams: orderParams,
        body: UpdatePurchaseOrderRequestSchema,
        responses: {
            200: PurchaseOrderSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a pre-commitment order (purchasing.edit). Editing an APPROVED order withdraws the approval (back to draft); a sent order answers 409 except a notes-only change (R8, BR-PUR-01-11a).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/purchases/orders/:orderId',
        pathParams: orderParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a pre-commitment order (purchasing.delete) — R8/BR-PUR-01-11a; sent+ answers 409.',
    },
    approve: {
        method: 'POST',
        path: '/api/purchases/orders/:orderId/approve',
        pathParams: orderParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PurchaseOrderSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Approve an over-limit draft (purchasing.approve, BR-PUR-01-01) — Draft → Approved, clearing the amber banner. Only meaningful strictly above the limit (PUR-01 §15).',
    },
    send: {
        method: 'POST',
        path: '/api/purchases/orders/:orderId/send',
        pathParams: orderParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: SendPurchaseOrderResponseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Send to the supplier (purchasing.edit) — Draft/Approved → Sent, the commitment door. Refused for an unapproved over-limit draft with the exact BR-PUR-01-01 sentence. From here the order counts toward the on-order quantity.',
    },
    cancel: {
        method: 'POST',
        path: '/api/purchases/orders/:orderId/cancel',
        pathParams: orderParams,
        /** GEN-59: never empty — the reason is the act's price of admission. */
        body: z
            .object({
            reason: z.string().trim().min(1, PO_MESSAGES.cancelReasonRequired).max(500),
        })
            .strict(),
        responses: {
            200: PurchaseOrderSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Cancel (purchasing.edit): voids a pre-commitment order, or cancels a SENT one (the legal correction of a commitment) unless posted bills hang off it. GEN-59: reason required, stored on the order and written to the audit row.',
    },
    /**
     * Wave 5E — AMEND a sent order: back to draft, so its lines and prices can
     * be rewritten and it can be sent again (the supplier then gets a
     * "revised" copy). Only while NOTHING has been acted on the commitment —
     * no posted goods receipt, no posted bill, no live import order — or the
     * blocking document is named and the correction stays cancel-and-reissue
     * (R8 / BR-PUR-01-11a). Mirrors the sales order's `reopen` (wave 4B).
     */
    reopen: {
        method: 'POST',
        path: '/api/purchases/orders/:orderId/reopen',
        pathParams: orderParams,
        body: z
            .object({
            reason: z.string().trim().min(1, PO_MESSAGES.reopenReasonRequired).max(500),
        })
            .strict(),
        responses: {
            200: PurchaseOrderSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Amend (purchasing.approve): a SENT order with no posted receipts, bills or import orders returns to draft; refused otherwise with PO_REFUSALS.reopenBlocked naming the document. Audit purchase_order.reopen carries the reason.',
    },
    /** Wave 3B — the list's tiles over the WHOLE register (see `document-stats.ts`). */
    stats: {
        method: 'GET',
        path: '/api/purchases/orders/stats',
        responses: {
            200: PurchaseOrderStatsSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'PUR-01 list tiles (purchasing.view): open orders and their value, awaiting receipt, received but not billed — base currency, in SQL.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=purchase-orders.js.map